import { useState, useEffect, useCallback } from 'react';

const useServerHealth = (autoRefresh = false, interval = 5000) => {
  const [metrics, setMetrics] = useState(null);
  const [dbActivity, setDbActivity] = useState({
    activities: [],
    slowQueries: [],
    totalActive: 0,
    slowQueryCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [metricsResponse, activityResponse] = await Promise.all([
        fetch('/api/health/metrics'),
        fetch('/api/health/db-activity')
      ]);

      if (!metricsResponse.ok) {
        throw new Error(`HTTP error! status: ${metricsResponse.status}`);
      }

      const metricsData = await metricsResponse.json();
      setMetrics(metricsData);
      setLastUpdated(new Date());

      if (activityResponse.ok) {
        const activityData = await activityResponse.json();
        setDbActivity(activityData);
      } else {
        setDbActivity({
          activities: [],
          slowQueries: [],
          totalActive: 0,
          slowQueryCount: 0
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching health metrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto refresh effect
  useEffect(() => {
    let intervalId;
    
    if (autoRefresh && interval > 0) {
      intervalId = setInterval(fetchMetrics, interval);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, interval, fetchMetrics]);

  const refresh = useCallback(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    dbActivity,
    loading,
    error,
    lastUpdated,
    refresh,
    fetchMetrics
  };
};

export default useServerHealth; 